import {
  CartProduct,
  FarmProduct,
  useCartState,
} from 'public/context/CartContext';
import { CouponDiscounts } from '../interfaces/Coupon';
import DiscountType from '../enums/discount-type';
import calculateVatFromGross from './pvnHelper';
import { ProductAmount } from '../types/ProductData';

type CartProductPriceType = 'PER_UNIT' | 'TOTAL';

interface ProductPriceTotalProps {
  totalWithDiscount: number;
  totalWithoutDiscount: number;
  totalVat: number;
  couponDiscount: number;
  totalWithCoupon: number; // total after coupon discount if no other discounts apply
}

type GroupedVatProps = {
  vatRate: number;
  totalVat: number;
};

interface PriceTotalProps extends ProductPriceTotalProps {
  groupedVats: GroupedVatProps[];
}

export interface FarmProductPriceTotalProps extends PriceTotalProps {
  farmCouponDiscount: number;
}

interface CalculateBasePriceProps {
  changedBasePrice: number | null;
  storageStockDiscountPrice: number | null;
}

export const isRenderablePrice = (
  price: number | null | undefined
): price is number => price != null && !isNaN(price);

export const calculateBasePrice = (
  productAmount: ProductAmount,
  amount: number
): CalculateBasePriceProps => {
  let changedBasePrice = productAmount?.storage_stock_price;
  let storageStockDiscountPrice = productAmount?.storage_stock_discount_price;

  if (productAmount?.wholesale_price_ranges) {
    const basePrice = productAmount?.wholesale_price_ranges.find(
      (priceRange) =>
        priceRange.amount_from <= amount && priceRange.amount_to >= amount
    );

    if (basePrice) {
      changedBasePrice = parseFloat(basePrice.price);

      if (
        changedBasePrice &&
        productAmount?.storage_stock_discount_price &&
        productAmount?.storage_stock_discount_price > changedBasePrice
      ) {
        storageStockDiscountPrice = null;
      }
    }
  }

  return { changedBasePrice, storageStockDiscountPrice };
};

const calculateProductPriceTotals = (
  product: CartProduct,
  type: CartProductPriceType = 'TOTAL',
  productCoupons: CouponDiscounts[] = [],
  marketType = 'EMARKET'
): ProductPriceTotalProps => {
  const { amount, cartProduct, vat } = product;
  const productAmount = cartProduct?.product_amounts?.find(
    (amount) => amount.type === marketType
  );

  const {
    storage_stock_price = 0,
    storage_stock_discount_price,
    storage_wholesale_limit,
    storage_wholesale_price,
  } = productAmount ?? {};

  const { changedBasePrice } = calculateBasePrice(productAmount!, amount);

  let totalVat = 0;
  let totalWithDiscount = Math.max(storage_stock_price, 0);
  let isCouponDiscountApplied = true;
  const totalWithoutDiscount = Math.max(
    changedBasePrice ? changedBasePrice : storage_stock_price,
    0
  );

  if (
    storage_stock_discount_price &&
    storage_stock_discount_price < totalWithDiscount
  ) {
    totalWithDiscount = Math.max(storage_stock_discount_price, 0);
    isCouponDiscountApplied = false;
  }

  if (
    storage_wholesale_limit &&
    storage_wholesale_price &&
    amount >= storage_wholesale_limit &&
    storage_wholesale_price < totalWithDiscount
  ) {
    totalWithDiscount = Math.max(storage_wholesale_price, 0);
    isCouponDiscountApplied = false;
  }

  if (changedBasePrice && totalWithDiscount > changedBasePrice) {
    totalWithDiscount = changedBasePrice;
  }

  if (vat) {
    totalVat = calculateVatFromGross(totalWithDiscount, vat);
  }

  // Optional coupon discount when no other discounts apply
  const hasOwnDiscount = totalWithDiscount < totalWithoutDiscount;

  const couponDiscount =
    isCouponDiscountApplied && !hasOwnDiscount
      ? productCoupons.reduce(
          (sum, c) =>
            sum + (c.productDiscounts?.[cartProduct?.id as number] ?? 0),
          0
        )
      : 0.0;

  const totalWithCoupon = totalWithDiscount - couponDiscount;

  if (type === 'PER_UNIT') {
    return {
      totalVat: Math.max(totalVat, 0),
      totalWithDiscount: Math.max(totalWithDiscount, 0),
      totalWithoutDiscount: Math.max(totalWithoutDiscount, 0),
      couponDiscount: Math.max(couponDiscount, 0),
      totalWithCoupon: Math.max(totalWithCoupon, 0),
    };
  }

  const lineGross = totalWithDiscount * amount;
  const lineVat = vat ? calculateVatFromGross(lineGross, vat) : 0;

  return {
    totalVat: Math.max(lineVat, 0),
    totalWithDiscount: Math.max(lineGross, 0),
    totalWithoutDiscount: Math.max(totalWithoutDiscount * amount, 0),
    couponDiscount: Math.max(couponDiscount * amount, 0),
    totalWithCoupon: Math.max(totalWithCoupon * amount, 0),
  };
};

export const calculateFarmPriceTotals = (
  farmId: number,
  products: CartProduct[],
  coupons: CouponDiscounts[] = []
): FarmProductPriceTotalProps => {
  const farmCoupons = coupons.filter(
    (c) => c.farm_id === farmId && c.apply_to_whole_cart
  );
  const productCoupons = coupons.filter(
    (c) => c.farm_id === farmId && !c.apply_to_whole_cart
  );

  const farmPriceTotals = products.reduce<FarmProductPriceTotalProps>(
    (acc, product) => {
      const productPriceTotals = calculateProductPriceTotals(
        product,
        'TOTAL',
        productCoupons,
        product.cartType
      );

      acc.totalWithDiscount += productPriceTotals.totalWithDiscount;
      acc.totalWithoutDiscount += productPriceTotals.totalWithoutDiscount;
      acc.totalVat += productPriceTotals.totalVat;
      acc.couponDiscount += productPriceTotals.couponDiscount;
      acc.totalWithCoupon += productPriceTotals.totalWithCoupon;

      if (product.vat && productPriceTotals.totalVat > 0) {
        const vatGroup = acc.groupedVats.find(
          (vatGroup) => vatGroup.vatRate === product.vat
        );

        if (vatGroup) {
          vatGroup.totalVat += Math.max(productPriceTotals.totalVat, 0);
        } else {
          const newVatGroup: GroupedVatProps = {
            vatRate: product.vat,
            totalVat: Math.max(productPriceTotals.totalVat, 0),
          };

          acc.groupedVats.push(newVatGroup);
        }
      }

      return acc;
    },
    {
      totalWithDiscount: 0,
      totalWithoutDiscount: 0,
      totalVat: 0,
      couponDiscount: 0,
      farmCouponDiscount: 0,
      totalWithCoupon: 0,
      groupedVats: [],
    }
  );

  farmCoupons.sort(sortCoupons).forEach((c) => {
    const farmCouponDiscount = calculateFarmCouponDiscount(
      c,
      farmId,
      farmPriceTotals.totalWithCoupon
    );

    if (farmCouponDiscount > 0) {
      farmPriceTotals.couponDiscount += farmCouponDiscount;
      farmPriceTotals.farmCouponDiscount += farmCouponDiscount;
      farmPriceTotals.totalWithCoupon -= farmCouponDiscount;
    }
  }, 0);

  return farmPriceTotals;
};

export const calculatePriceTotals = (
  farmProducts: FarmProduct[]
): PriceTotalProps => {
  return farmProducts.reduce<PriceTotalProps>(
    (acc, farm) => {
      acc.totalWithDiscount += farm.farmPriceTotals.totalWithDiscount;
      acc.totalWithoutDiscount += farm.farmPriceTotals.totalWithoutDiscount;
      acc.totalVat += farm.farmPriceTotals.totalVat;
      acc.couponDiscount += farm.farmPriceTotals.couponDiscount;
      acc.totalWithCoupon += farm.farmPriceTotals.totalWithCoupon;

      Object.values(farm.farmPriceTotals.groupedVats).forEach((groupedVat) => {
        const vatGroup = acc.groupedVats.find(
          (vatGroup) => vatGroup.vatRate === groupedVat.vatRate
        );

        if (vatGroup) {
          vatGroup.totalVat += Math.max(groupedVat.totalVat, 0);
        } else {
          const newVatGroup: GroupedVatProps = {
            vatRate: groupedVat.vatRate,
            totalVat: Math.max(groupedVat.totalVat, 0),
          };

          acc.groupedVats.push(newVatGroup);
        }
      });

      return acc;
    },
    {
      totalWithDiscount: 0,
      totalWithoutDiscount: 0,
      totalVat: 0,
      groupedVats: [],
      couponDiscount: 0,
      totalWithCoupon: 0,
    }
  );
};

export const sortCoupons = (a: CouponDiscounts, b: CouponDiscounts): number => {
  if (a.apply_to_whole_cart && !b.apply_to_whole_cart) {
    return 1;
  }

  if (!a.apply_to_whole_cart && b.apply_to_whole_cart) {
    return -1;
  }

  if (
    a.discount_type === DiscountType.PERCENT &&
    b.discount_type !== DiscountType.PERCENT
  ) {
    return 1;
  }

  if (
    a.discount_type !== DiscountType.PERCENT &&
    b.discount_type === DiscountType.PERCENT
  ) {
    return -1;
  }

  return 0;
};

export const calculateFarmCouponDiscount = (
  coupon: CouponDiscounts,
  farmId: number,
  totalWithCoupon: number
): number => {
  if (coupon.farm_id !== farmId) {
    return 0;
  }

  const couponDiscountValue = Number(coupon.discount_value) ?? 0;

  if (coupon.discount_type === DiscountType.FIXED) {
    return couponDiscountValue;
  }

  const discount = (totalWithCoupon * (couponDiscountValue / 100)).toFixed(2);

  return Number(discount);
};

export const calculateTotalPrice = (farmProducts: FarmProduct[]): number => {
  return Number(
    farmProducts
      .reduce(
        (total, farm) =>
          total +
          Math.max(
            farm.farmPriceTotals?.totalWithDiscount -
              (farm.farmPriceTotals?.couponDiscount ?? 0),
            0
          ) +
          (farm.selectedDelivery?.price || 0),
        0
      )
      .toFixed(2)
  );
};

interface CartProductPriceProps {
  product: CartProduct;
  type?: CartProductPriceType;
}

export const CartProductPrice = ({
  product,
  type = 'TOTAL',
}: CartProductPriceProps) => {
  const { coupons } = useCartState();
  const { totalWithoutDiscount, totalWithDiscount, totalWithCoupon } =
    calculateProductPriceTotals(product, type, coupons, product.cartType);

  const cartProductAmount = product?.cartProduct?.product_amounts?.find(
    (amount) => amount.type === product.cartType
  );

  const hasCouponPrice =
    typeof totalWithCoupon === 'number' &&
    totalWithCoupon < totalWithoutDiscount;
  const finalPrice = hasCouponPrice ? totalWithCoupon : totalWithDiscount;
  const hasAnyDiscount =
    hasCouponPrice || totalWithDiscount < totalWithoutDiscount;

  const unitLabel =
    type === 'PER_UNIT' && cartProductAmount?.storage_unit
      ? `\u00A0/\u00A0${cartProductAmount.storage_unit}`
      : null;

  if (!hasAnyDiscount) {
    return (
      <div className="price-type-container d-flex flex-row">
        <div className="price">
          {totalWithoutDiscount.toFixed(2)} <span>€</span>
        </div>
        {unitLabel && <div className="type-of-price">{unitLabel}</div>}
      </div>
    );
  }

  return (
    <div className="price-type-wrapper d-flex flex-col">
      <div className="price-type-second d-flex flex-row">
        <div className="price original">
          {totalWithoutDiscount.toFixed(2)} €
        </div>
        {unitLabel && <div className="type-of-price">{unitLabel}</div>}
      </div>
      <div className="price-grey grey-price d-flex flex-row">
        <div className="price">
          {finalPrice.toFixed(2)} <span>€</span>
        </div>
        {unitLabel && <div className="type-of-price">{unitLabel}</div>}
      </div>
    </div>
  );
};
